import { useMemo, useState } from 'react';
import type {
  InsightsBrandFunnelRow,
  InsightsBrandFunnelsResponse,
} from '@brand24-original/llm-types';
import { AllBrandsStatsResponse } from '../../../shared/models/projects/stats/AllBrandsStatsResponse';
import { buildBrandColors } from '../../../shared/components/report/brandColors';
import { selectPieChartTopItems } from '../../prompt/preview/components/stats/helpers/StatsHelper';

/**
 * Size of the default selection (top by visibility) when a section does not pass its own. Not a cap —
 * the user may tick any number of brands.
 */
const DEFAULT_PICKER_BRANDS = 10;

/**
 * Wspólna selekcja marek dla sekcji porównujących konkurentów ("Funnel vs competitors", macierz
 * promptów) — każda sekcja trzyma WŁASNĄ instancję (niezależny wybór, decyzja usera).
 * Lista pickera = marki, które pokazałby pie chart SoV na overview (reguła top-15 z forsowaną
 * marką — selectPieChartTopItems), plus marka analizowana (focusBrandKey = wybór ChangeKeywordBox) zawsze; brak danych SoV (błąd / zapytanie
 * wyłączone) → bez filtra, żeby awaria SoV nie wyłączała sekcji. Wybór: domyślnie top 10 po
 * widoczności z wymuszoną marką analizowaną, bez górnego limitu zaznaczeń (decyzja usera); override
 * przycinany do aktualnej listy (duchy po zmianie filtrów rysowałyby puste wykresy).
 * `defaultSelectionSize` overrides the default selection size (the analyzed brand always counts as one
 * of them) — the competitor funnel chart uses 5, more lines on one chart are unreadable.
 */
export const useBrandSelection = (
  data: InsightsBrandFunnelsResponse | null,
  sovBrands: AllBrandsStatsResponse[] | null,
  defaultSelectionSize: number = DEFAULT_PICKER_BRANDS
) => {
  // null = użytkownik nic nie zmieniał → obowiązuje domyślny wybór (przelicza się z danymi).
  const [selectedOverride, setSelectedOverride] = useState<string[] | null>(null);

  const focusBrandKey = data?.focusBrandKey ?? '';

  const sortedBrands = useMemo(
    () => (data ? [...data.brands].sort((a, b) => b.visible - a.visible) : []),
    [data]
  );

  const sovAllowedKeys = useMemo(() => {
    if (!sovBrands || sovBrands.length === 0) return null;
    const { topItems } = selectPieChartTopItems(
      sovBrands.map((stats) => ({ name: stats.keyword, value: stats.shareOfVoice })),
      focusBrandKey
    );
    return new Set(topItems.map((item) => item.name));
  }, [sovBrands, focusBrandKey]);

  const pickerBrands = useMemo<InsightsBrandFunnelRow[]>(
    () =>
      sovAllowedKeys
        ? sortedBrands.filter(
            (brand) => brand.brandKey === focusBrandKey || sovAllowedKeys.has(brand.brandKey)
          )
        : sortedBrands,
    [sortedBrands, sovAllowedKeys, focusBrandKey]
  );

  const defaultKeys = useMemo(() => {
    const top = pickerBrands.slice(0, defaultSelectionSize).map((brand) => brand.brandKey);
    return !focusBrandKey || top.includes(focusBrandKey)
      ? top
      : [focusBrandKey, ...top.slice(0, defaultSelectionSize - 1)];
  }, [pickerBrands, focusBrandKey, defaultSelectionSize]);

  const selectedKeys = useMemo(() => {
    if (!selectedOverride) return defaultKeys;
    const available = new Set(pickerBrands.map((brand) => brand.brandKey));
    const trimmed = selectedOverride.filter((key) => available.has(key));
    return !focusBrandKey || trimmed.includes(focusBrandKey) ? trimmed : [focusBrandKey, ...trimmed];
  }, [selectedOverride, defaultKeys, pickerBrands, focusBrandKey]);

  const selectedBrands = useMemo(
    () => pickerBrands.filter((brand) => selectedKeys.includes(brand.brandKey)),
    [pickerBrands, selectedKeys]
  );

  // Brand colors as in the reports — ONE map from the picker list, shared by the picker rows and the
  // matrix headers so a brand never changes color between them. Visibility DESC is the canonical
  // order for Insights (the counterpart of the SoV ranking that canonizes report colors).
  const brandColors = useMemo(
    () =>
      buildBrandColors(
        pickerBrands.map((brand) => ({
          brand: brand.label,
          isTracked: brand.brandKey === focusBrandKey,
        }))
      ),
    [pickerBrands, focusBrandKey]
  );

  // The picker (CheckboxSelect) hands back the full new list; it guards the own brand itself
  // (`fixed`), and the `selectedKeys` memo forces the own brand in anyway.
  const selectBrands = (brandKeys: string[]) => setSelectedOverride(brandKeys);

  return { focusBrandKey, pickerBrands, selectedKeys, selectedBrands, brandColors, selectBrands };
};
